import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, UserPlus, Trash2, Shield, User as UserIcon, Loader2, ShieldCheck, ShieldOff, RotateCcw, Link2, Copy, Ban, KeyRound, Lock } from "lucide-react";
import { passwordError, PW_HINT } from "@/lib/password";

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [role, setRole] = useState("user");
  const [creating, setCreating] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [toReset, setToReset] = useState(null);
  const [toResetPw, setToResetPw] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");

  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("user");
  const [inviting, setInviting] = useState(false);

  const load = async () => {
    try {
      const [u, inv] = await Promise.all([api.get("/users"), api.get("/invites")]);
      setUsers(u.data);
      setInvites(inv.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createUser = async (e) => {
    e.preventDefault();
    const pErr = passwordError(password);
    if (pErr) { toast.error(pErr); return; }
    if (password !== password2) { toast.error("Passwords do not match"); return; }
    setCreating(true);
    try {
      await api.post("/users", { username: username.trim(), password, role });
      toast.success(`User "${username.trim().toLowerCase()}" created — must set new password & 2FA on first login`);
      setUsername(""); setPassword(""); setPassword2(""); setRole("user");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const del = async () => {
    const id = toDelete.id; setToDelete(null);
    try { await api.delete(`/users/${id}`); toast.success("User removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const reset2fa = async () => {
    const u = toReset; setToReset(null);
    try { await api.post(`/users/${u.id}/reset-2fa`); toast.success(`2FA reset for "${u.username}" — they'll re-enroll on next login`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const resetPw = async () => {
    const u = toResetPw;
    const pErr = passwordError(newPw);
    if (pErr) { toast.error(pErr); return; }
    try {
      await api.patch(`/users/${u.id}/password`, { password: newPw });
      toast.success(`Password reset for "${u.username}" — they must change it on next login`);
      setToResetPw(null); setNewPw(""); setNewPw2("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const inviteLink = (token) => `${window.location.origin}/invite/${token}`;

  const copyLink = async (token) => {
    await navigator.clipboard.writeText(inviteLink(token));
    toast.success("Invite link copied to clipboard");
  };

  const createInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const { data } = await api.post("/invites", { username: inviteEmail.trim(), role: inviteRole });
      await navigator.clipboard.writeText(inviteLink(data.token)).catch(() => {});
      toast.success("Invite link created & copied — share it with the user");
      setInviteEmail(""); setInviteRole("user");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setInviting(false);
    }
  };

  const revokeInvite = async (id) => {
    try { await api.delete(`/invites/${id}`); toast.success("Invite revoked"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const STATUS_STYLE = {
    pending: "text-primary border-primary/40",
    used: "text-green-400 border-green-900/60",
    expired: "text-muted-foreground border-border",
  };

  return (
    <div className="min-h-screen w-full bg-background text-foreground">
      <header className="h-12 border-b border-border flex items-center px-4 gap-3">
        <Link to="/" data-testid="back-link">
          <Button variant="ghost" size="sm" className="rounded-none h-8 text-xs text-muted-foreground"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Scrubber</Button>
        </Link>
        <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground ml-auto">User Management</span>
      </header>

      <div className="max-w-3xl mx-auto p-6 md:p-8 space-y-8">
        <div className="border border-border bg-surface">
          <div className="px-4 py-3 border-b border-border">
            <h2 className="text-sm font-semibold tracking-tight flex items-center gap-2"><UserPlus className="h-4 w-4 text-primary" /> Add User</h2>
          </div>
          <form onSubmit={createUser} className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Email (username)</Label>
              <Input data-testid="new-username-input" type="email" placeholder="user@company.com" value={username} onChange={(e) => setUsername(e.target.value)} required className="font-mono text-xs rounded-none bg-background border-border h-9 focus-visible:ring-primary" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Temp Password</Label>
              <Input data-testid="new-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="font-mono text-xs rounded-none bg-background border-border h-9 focus-visible:ring-primary" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Confirm Password</Label>
              <Input data-testid="new-password2-input" type="password" value={password2} onChange={(e) => setPassword2(e.target.value)} required className="font-mono text-xs rounded-none bg-background border-border h-9 focus-visible:ring-primary" />
            </div>
            <p className="sm:col-span-2 text-[10px] text-muted-foreground -mt-1">{PW_HINT}</p>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Role</Label>
              <div className="flex">
                {["user", "admin"].map((r) => (
                  <button key={r} type="button" data-testid={`role-${r}`} onClick={() => setRole(r)}
                    className={`flex-1 text-[10px] uppercase tracking-[0.15em] h-9 border transition-colors duration-100 ${role === r ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground"}`}>{r}</button>
                ))}
              </div>
            </div>
            <div className="flex items-end">
              <Button type="submit" data-testid="create-user-button" disabled={creating || !password || password !== password2} className="w-full rounded-none h-9 bg-primary text-primary-foreground hover:bg-accent-hover font-medium">
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
              </Button>
            </div>
          </form>
          <p className="px-4 pb-3 text-[11px] text-muted-foreground">New users are forced to set a new password and enroll Google/Microsoft Authenticator 2FA on first login.</p>
        </div>

        <div className="border border-border bg-surface">
          <div className="px-4 py-3 border-b border-border">
            <h2 className="text-sm font-semibold tracking-tight flex items-center gap-2"><Link2 className="h-4 w-4 text-primary" /> Invite by Link</h2>
          </div>
          <form onSubmit={createInvite} className="p-4 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Email</Label>
              <Input data-testid="invite-email-input" type="email" placeholder="user@company.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} required className="font-mono text-xs rounded-none bg-background border-border h-9 focus-visible:ring-primary" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Role</Label>
              <div className="flex">
                {["user", "admin"].map((r) => (
                  <button key={r} type="button" data-testid={`invite-role-${r}`} onClick={() => setInviteRole(r)}
                    className={`flex-1 text-[10px] uppercase tracking-[0.15em] h-9 border transition-colors duration-100 ${inviteRole === r ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground"}`}>{r}</button>
                ))}
              </div>
            </div>
            <Button type="submit" data-testid="create-invite-button" disabled={inviting} className="rounded-none h-9 bg-primary text-primary-foreground hover:bg-accent-hover font-medium">
              {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate Link"}
            </Button>
          </form>
          <p className="px-4 pb-3 text-[11px] text-muted-foreground">Link is copied to your clipboard and valid for 7 days. The recipient sets their own password, then enrolls their authenticator app on first sign-in.</p>

          {invites.length > 0 && (
            <div className="border-t border-border divide-y divide-border" data-testid="invites-list">
              {invites.map((inv) => (
                <div key={inv.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`invite-row-${inv.username}`}>
                  <span className="font-mono text-xs truncate">{inv.username}</span>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground border border-border px-1.5 py-0.5">{inv.role}</span>
                  <span className={`text-[10px] uppercase tracking-[0.15em] border px-1.5 py-0.5 ${STATUS_STYLE[inv.status]}`} data-testid={`invite-status-${inv.username}`}>{inv.status}</span>
                  <div className="ml-auto flex items-center gap-1">
                    {inv.status === "pending" && (
                      <Button data-testid={`copy-invite-${inv.username}`} onClick={() => copyLink(inv.token)} variant="ghost" size="sm" className="h-8 rounded-none text-xs text-muted-foreground hover:text-primary"><Copy className="h-3.5 w-3.5 mr-1" /> Copy link</Button>
                    )}
                    <Button data-testid={`revoke-invite-${inv.username}`} onClick={() => revokeInvite(inv.id)} variant="ghost" size="icon" className="h-8 w-8 rounded-none text-muted-foreground hover:text-red-400"><Ban className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border border-border bg-surface">
          <div className="px-4 py-3 border-b border-border"><h2 className="text-sm font-semibold tracking-tight">Allowed Users ({users.length})</h2></div>
          {loading ? (
            <div className="p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="divide-y divide-border" data-testid="users-list">
              {users.map((u) => (
                <div key={u.id} className="flex items-center gap-3 px-4 py-3" data-testid={`user-row-${u.username}`}>
                  {u.role === "admin" ? <Shield className="h-4 w-4 text-primary" /> : <UserIcon className="h-4 w-4 text-muted-foreground" />}
                  <span className="font-mono text-sm">{u.username}</span>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground border border-border px-1.5 py-0.5">{u.role}</span>
                  {u.totp_enabled ? (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-green-400" data-testid={`twofa-status-${u.username}`}><ShieldCheck className="h-3.5 w-3.5" /> 2FA on</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground" data-testid={`twofa-status-${u.username}`}><ShieldOff className="h-3.5 w-3.5" /> 2FA pending</span>
                  )}
                  {u.locked && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-red-400 border border-red-900/60 px-1.5 py-0.5" data-testid={`locked-status-${u.username}`}><Lock className="h-3.5 w-3.5" /> Locked</span>
                  )}
                  <div className="ml-auto flex items-center gap-1">
                    {u.totp_enabled && (
                      <Button data-testid={`reset-2fa-${u.username}`} onClick={() => setToReset(u)} variant="ghost" size="sm" className="h-8 rounded-none text-xs text-muted-foreground hover:text-primary"><RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset 2FA</Button>
                    )}
                    <Button data-testid={`reset-pw-${u.username}`} onClick={() => { setToResetPw(u); setNewPw(""); }} variant="ghost" size="sm" className="h-8 rounded-none text-xs text-muted-foreground hover:text-primary"><KeyRound className="h-3.5 w-3.5 mr-1" /> Reset PW</Button>
                    {u.id !== user.id && (
                      <Button data-testid={`delete-user-${u.username}`} onClick={() => setToDelete(u)} variant="ghost" size="icon" className="h-8 w-8 rounded-none text-muted-foreground hover:text-red-400"><Trash2 className="h-4 w-4" /></Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-none bg-surface border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove user?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.username}" will lose access to the scrubber. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-user" onClick={del} className="rounded-none bg-red-500 hover:bg-red-600 text-white">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!toReset} onOpenChange={(o) => !o && setToReset(null)}>        <AlertDialogContent className="rounded-none bg-surface border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset 2FA?</AlertDialogTitle>
            <AlertDialogDescription>"{toReset?.username}" will need to set up Google Authenticator again on their next login.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-reset-2fa" onClick={reset2fa} className="rounded-none bg-primary text-primary-foreground hover:bg-accent-hover">Reset 2FA</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!toResetPw} onOpenChange={(o) => { if (!o) { setToResetPw(null); setNewPw(""); setNewPw2(""); } }}>
        <AlertDialogContent className="rounded-none bg-surface border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset password</AlertDialogTitle>
            <AlertDialogDescription>Set a temporary password for "{toResetPw?.username}". They must change it on their next login.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Input data-testid="reset-pw-input" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="temporary password (min 4 chars)" className="font-mono rounded-none bg-background border-border focus-visible:ring-primary" />
            <Input data-testid="reset-pw2-input" type="password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)} placeholder="confirm temporary password" className="font-mono rounded-none bg-background border-border focus-visible:ring-primary" />
            {newPw2.length > 0 && newPw !== newPw2 && <p className="text-[11px] text-red-400">Passwords do not match</p>}
            <p className="text-[10px] text-muted-foreground">{PW_HINT}</p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-reset-pw" disabled={!!passwordError(newPw) || newPw !== newPw2} onClick={resetPw} className="rounded-none bg-primary text-primary-foreground hover:bg-accent-hover">Set Password</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
